/**
 * Downscale base64 image data URLs to small JPEG thumbnails for PDF embedding.
 *
 * The server hands us full-resolution images as `data:` URLs (it fetches the
 * S3 bytes so the browser never has to, sidestepping CORS). Embedding those
 * full-res into a PDF would bloat it, so we redraw each onto a canvas at
 * thumbnail size. `data:` URLs do NOT taint a canvas, so `toDataURL` works
 * here even though it would fail on a cross-origin S3 URL.
 *
 * Defensive: any failure returns null (the image is simply skipped) — never
 * throws, never blocks the export. Returning null rather than the original is
 * deliberate: the consumer is a PDF renderer that only supports JPEG/PNG, so a
 * passed-through WebP/GIF/SVG original could crash the whole render. We only
 * ever hand back a freshly-encoded JPEG, or nothing.
 */

const DEFAULT_MAX_PX = 160;
const DEFAULT_QUALITY = 0.72;
const DEFAULT_CONCURRENCY = 6;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

/** Redraw one data URL as a small JPEG thumbnail; returns null on any failure. */
export async function downscaleDataUrl(
  dataUrl: string,
  maxPx = DEFAULT_MAX_PX,
  quality = DEFAULT_QUALITY
): Promise<string | null> {
  try {
    const img = await loadImage(dataUrl);
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) return null;

    const scale = Math.min(1, maxPx / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    const out = canvas.toDataURL('image/jpeg', quality);
    // Guard against environments where toDataURL returns an empty/invalid value.
    return out.startsWith('data:image/jpeg') ? out : null;
  } catch {
    return null;
  }
}

/**
 * Downscale many data URLs with bounded concurrency. Preserves index alignment;
 * null/empty inputs pass through as null.
 */
export async function downscaleAll(
  urls: Array<string | null | undefined>,
  maxPx = DEFAULT_MAX_PX,
  quality = DEFAULT_QUALITY,
  concurrency = DEFAULT_CONCURRENCY
): Promise<Array<string | null>> {
  const out: Array<string | null> = new Array(urls.length).fill(null);
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < urls.length) {
      const idx = cursor++;
      const u = urls[idx];
      out[idx] = u ? await downscaleDataUrl(u, maxPx, quality) : null;
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, urls.length) }, worker));
  return out;
}
